import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  User,
  Search,
  ArrowRight,
  BookOpen
} from 'lucide-react';

import { blogs } from '../data/blogs';

const BlogPage = () => {

  const { i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';

  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setPosts(blogs);
  }, []);

  const filteredPosts = posts.filter((post) => {

    const title = isHindi
      ? post.title_hi.toLowerCase()
      : post.title_en.toLowerCase();

    return title.includes(searchTerm.toLowerCase());

  });

  return (

    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 lg:px-24 pb-20">

      <div className="max-w-7xl mx-auto">

        {/* HERO SECTION */}
        <div className="text-center mb-16">

          <div className="flex justify-center mb-5">

            <div className="bg-[#C9A84C]/10 p-4 rounded-full">
              <BookOpen className="text-[#C9A84C]" size={34} />
            </div>

          </div>

          <h1
            className="text-4xl md:text-6xl font-serif text-[#F5F2EB] mb-5"
            data-testid="blog-heading"
          >
            {isHindi
              ? 'ज्योतिष एवं वास्तु ब्लॉग'
              : 'Astrology & Vastu Blog'}
          </h1>

          <p className="text-[#A3A3A3] max-w-3xl mx-auto leading-relaxed text-lg">
            {isHindi
              ? 'ज्योतिष, वास्तु, आध्यात्मिकता और जीवन मार्गदर्शन से जुड़ी उपयोगी जानकारी, उपाय एवं प्रेरणादायक लेख पढ़ें।'
              : 'Explore insightful articles on Astrology, Vastu Shastra, Spirituality, Remedies, and Life Guidance for peace, prosperity, and personal growth.'}
          </p>

        </div>

        {/* SEARCH BAR */}
        <div className="max-w-2xl mx-auto mb-16">

          <div className="relative">

            <Search
              className="absolute left-5 top-1/2 -translate-y-1/2 text-[#777]"
              size={18}
            />

            <input
              type="text"
              placeholder={
                isHindi
                  ? 'ब्लॉग खोजें...'
                  : 'Search articles...'
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#111111] border border-[#C9A84C]/10 rounded-full px-14 py-5 text-[#F5F2EB] outline-none focus:border-[#C9A84C]/40 transition-all"
            />

          </div>

        </div>

        {/* BLOG GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">

          {filteredPosts.map((post) => (

            <Link
              key={post.post_id}
              to={`/blog/${post.post_id}`}
              className="group bg-[#111111] border border-[#C9A84C]/10 rounded-3xl overflow-hidden hover:border-[#C9A84C]/30 transition-all duration-300 hover:-translate-y-2"
            >

              {/* BLOG IMAGE */}
              <div className="relative h-60 overflow-hidden">

               <img
                src={post.image}
                alt={isHindi ? post.title_hi : post.title_en}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />


                <div className="absolute top-4 left-4">

                  <span className="bg-[#0A0A0A]/80 backdrop-blur-md text-[#C9A84C] text-xs uppercase tracking-wider px-4 py-2 rounded-full">
                    {post.category}
                  </span>

                </div>

              </div>

              {/* CONTENT */}
              <div className="p-7">

                <h2 className="text-2xl font-serif text-[#F5F2EB] mb-4 leading-snug group-hover:text-[#C9A84C] transition-colors">

                  {isHindi
                    ? post.title_hi
                    : post.title_en}

                </h2>

                <p className="text-[#A3A3A3] leading-relaxed line-clamp-4 mb-6">

                  {(isHindi
                    ? post.content_hi
                    : post.content_en
                  ).substring(0, 180)}...

                </p>

                {/* META */}
                <div className="flex items-center justify-between pt-5 border-t border-[#C9A84C]/10">

                  <div className="space-y-2">

                    <div className="flex items-center gap-2 text-[#888] text-sm">

                      <User size={14} />

                      <span>{post.author}</span>

                    </div>

                    <div className="flex items-center gap-2 text-[#888] text-sm">

                      <Calendar size={14} />

                      <span>{post.created_at}</span>

                    </div>

                  </div>

                  <div className="flex items-center gap-2 text-[#C9A84C] group-hover:translate-x-1 transition-transform">

                    <span className="text-sm">
                      {isHindi
                        ? 'और पढ़ें'
                        : 'Read More'}
                    </span>

                    <ArrowRight size={16} />

                  </div>

                </div>

              </div>

            </Link>

          ))}

        </div>

        {/* EMPTY STATE */}
        {filteredPosts.length === 0 && (

          <div className="text-center py-24">

            <h3 className="text-2xl text-[#F5F2EB] mb-3">

              {isHindi
                ? 'कोई ब्लॉग नहीं मिला'
                : 'No Blogs Found'}

            </h3>

            <p className="text-[#777]">

              {isHindi
                ? 'कृपया कोई अन्य शब्द खोजें'
                : 'Please try searching with another keyword'}

            </p>

          </div>

        )}

      </div>

    </div>

  );
};

export default BlogPage;